import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight, Calendar, PlayCircle, Images } from "lucide-react";
import { Reveal, Overline } from "@/components/public/Reveal";
import VideoBadge from "@/components/public/VideoBadge";
import Seo from "@/components/Seo";
import api, { fileUrl } from "@/lib/api";

const FALLBACK = "https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=1200&q=80";

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" }) : "";

export default function Actualites() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/realisations").then((r) => setItems(r.data)); }, []);

  const [lead, ...rest] = items;

  return (
    <div className="pt-32 md:pt-40 pb-24 bg-cream">
      <Seo title="Actualités & chantiers récents" path="/actualites"
        description="Suivez les derniers chantiers réalisés par Acanthe Jardins : galeries photo et vidéo, interviews de nos clients et partenaires, détail des travaux menés." />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="max-w-2xl mb-12">
          <Overline>Actualités & chantiers</Overline>
          <h1 className="mt-4 font-display text-5xl lg:text-6xl text-forest leading-[1.02]">Nos derniers chantiers</h1>
          <p className="mt-5 text-lg text-stone leading-relaxed">Découvrez nos réalisations les plus récentes : galeries, vidéos, interviews et le détail des travaux menés sur le terrain.</p>
        </Reveal>

        {items.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-border bg-white py-24 text-center">
            <p className="text-stone">Nos actualités de chantier arrivent très bientôt.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {lead && (
              <Reveal>
                <Link to={`/realisations/${lead.slug}`} data-testid={`actu-card-${lead.slug}`}
                  className="group grid lg:grid-cols-2 rounded-[28px] overflow-hidden bg-forest text-cream hover:shadow-2xl hover:shadow-forest/20 transition-all">
                  <div className="relative min-h-[300px] lg:min-h-[420px] overflow-hidden">
                    <img src={fileUrl(lead.cover_image) || FALLBACK} alt={lead.title} className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <span className="absolute top-5 left-5 rounded-full bg-cream/90 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-brand">{lead.category}</span>
                    <VideoBadge r={lead} />
                  </div>
                  <div className="p-8 lg:p-12 flex flex-col justify-center">
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-flora"><Calendar size={13} />{fmtDate(lead.created_at)}</span>
                    <h2 className="mt-3 font-display text-3xl lg:text-4xl leading-tight">{lead.title}</h2>
                    {lead.location && <p className="mt-2 text-cream/60 flex items-center gap-1.5"><MapPin size={15} />{lead.location}</p>}
                    <p className="mt-4 text-cream/80 leading-relaxed line-clamp-3">{lead.summary}</p>
                    <MediaBadges r={lead} className="mt-5 text-cream/70" />
                    <span className="mt-6 inline-flex items-center gap-2 font-semibold text-flora group-hover:gap-3 transition-all">Voir le chantier <ArrowRight size={18} /></span>
                  </div>
                </Link>
              </Reveal>
            )}

            {rest.length > 0 && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {rest.map((r, i) => (
                  <Reveal key={r.id} delay={(i % 3) * 0.08}>
                    <Link to={`/realisations/${r.slug}`} data-testid={`actu-card-${r.slug}`}
                      className="group flex flex-col h-full rounded-[22px] overflow-hidden bg-white border border-border hover:shadow-xl hover:shadow-forest/5 hover:-translate-y-1.5 transition-all">
                      <div className="relative aspect-[4/3] overflow-hidden">
                        <img src={fileUrl(r.cover_image) || FALLBACK} alt={r.title} className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-700" />
                        <span className="absolute top-4 left-4 rounded-full bg-cream/90 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-brand">{r.category}</span>
                        <VideoBadge r={r} />
                      </div>
                      <div className="p-6 flex flex-col flex-1">
                        <span className="inline-flex items-center gap-1.5 text-xs text-stone"><Calendar size={12} />{fmtDate(r.created_at)}</span>
                        <h3 className="mt-1 font-display text-2xl text-forest leading-tight">{r.title}</h3>
                        {r.location && <p className="text-sm text-stone flex items-center gap-1 mt-1"><MapPin size={13} />{r.location}</p>}
                        <p className="text-sm text-stone mt-2 line-clamp-2 flex-1">{r.summary}</p>
                        <MediaBadges r={r} className="mt-3 text-stone" />
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const MediaBadges = ({ r, className = "" }) => {
  const nVid = (r.interviews?.filter((v) => v.url).length || 0) + (r.video_gallery?.filter(Boolean).length || 0);
  const nImg = r.gallery?.length || 0;
  if (!nVid && !nImg && !(r.tasks?.length)) return null;
  return (
    <div className={`flex flex-wrap items-center gap-3 text-xs ${className}`}>
      {nVid > 0 && <span className="inline-flex items-center gap-1"><PlayCircle size={14} />{nVid} vidéo{nVid > 1 ? "s" : ""}</span>}
      {nImg > 0 && <span className="inline-flex items-center gap-1"><Images size={14} />{nImg} photo{nImg > 1 ? "s" : ""}</span>}
      {r.tasks?.length > 0 && <span className="inline-flex items-center gap-1">{r.tasks.length} tâches</span>}
    </div>
  );
};
